/* screens/academy/LessonEditor.jsx — редактор урока (дровер 2-го уровня).
   Секции: Контент · Видео · Материалы · Тест · ДЗ. Save — в футере дровера. */
import React from 'react';
import { Drawer } from '../../components/ui/Drawer.jsx';
import { QueryBoundary } from '../../components/patterns/States.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import { Field, TextInput, Textarea, NativeSelect, Checkbox } from '../../components/ui/Field.jsx';
import { ChatMarkdown } from '../../components/patterns/chat/Markdown.jsx';
import { useToast } from '../../app/providers/ToastProvider.jsx';
import { useConfirm } from '../../app/providers/ConfirmProvider.jsx';
import { hooks } from '../../hooks/index.jsx';
import * as api from '../../lib/api/academy.js';
import { videoMaxMb } from '../../lib/api/academy.js';
import { useAuth } from '../../app/providers/AuthProvider.jsx';

const { useState, useCallback, useEffect } = React;
const { useFakeQuery } = hooks;

const QTYPES = [
  { value: 'single', label: 'Один из вариантов' },
  { value: 'multi', label: 'Несколько из вариантов' },
  { value: 'bool', label: 'Верно / неверно' },
  { value: 'text', label: 'Короткий ответ' },
  { value: 'number', label: 'Число' },
];
const QTYPE_LABEL = Object.fromEntries(QTYPES.map((q) => [q.value, q.label]));

function Section({ icon, title, hint, children }) {
  return (
    <section className="acad-section">
      <div className="acad-section__head">
        <Icon name={icon} size={15} /><span className="acad-section__title">{title}</span>
        {hint != null && <><span className="acad-section__spacer" /><span className="acad-section__hint">{hint}</span></>}
      </div>
      {children}
    </section>
  );
}

const isChoice = (t) => t === 'single' || t === 'multi';

/* AD-3. Удаление контента, по которому уже есть работы учеников: бэк отвечает
   code='has_student_data' и ждёт повтора с ?force=1. Раньше клиент этого параметра не
   имел — методист читал инструкцию «повторите с ?force=1», выполнить которую было нечем.
   Первый заход идёт без force; на гарде показываем диалог с числом затронутых учеников
   и повторяем. Отмена в диалоге не удаляет ничего. */
async function deleteWithGuard({ del, confirm, toast, entity, onDone }) {
  try { await del(false); onDone(); return true; }
  catch (e) {
    if (!api.isStudentDataGuard(e)) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); return false; }
    const ok = await confirm({ title: 'Удалить вместе с работами учеников?',
      body: api.studentDataPrompt(e, entity), confirmLabel: 'Удалить с результатами', danger: true });
    if (!ok) return false;
    try { await del(true); toast.show('Удалено вместе с работами учеников', 'muted'); onDone(); return true; }
    catch (e2) { toast.show(e2?.detail || e2?.message || 'Ошибка', 'danger'); return false; }
  }
}

// #7: тест без вопросов / с 0 суммарными баллами ученику НЕ показывается (services.quiz_has_content).
// Подсказываем методисту, что черновик теста есть, но он пуст. Чистая ф-я → легко проверять.
function quizIsEmpty(quiz) {
  if (!quiz) return false;
  const questions = quiz.questions || [];
  if (questions.length === 0) return true;
  const totalPoints = questions.reduce((sum, q) => sum + (Number(q.points) || 0), 0);
  return totalPoints <= 0;
}

// ── редактор вопроса ─────────────────────────────────────────────────────────
function QuestionEditor({ quizId, question, num, onSaved }) {
  const toast = useToast();
  const confirm = useConfirm();
  const isNew = !question;
  const [text, setText] = useState(question?.text || '');
  const [qtype, setQtype] = useState(question?.qtype || 'single');
  const [options, setOptions] = useState(
    question?.options?.length ? question.options : [{ id: 'o1', text: '' }, { id: 'o2', text: '' }]);
  const [correctSet, setCorrectSet] = useState(new Set(
    Array.isArray(question?.correct) && isChoice(question?.qtype) ? question.correct.map(String) : []));
  const [boolCorrect, setBoolCorrect] = useState(question?.qtype === 'bool' ? !!(question.correct?.[0]) : true);
  const [textCorrect, setTextCorrect] = useState(question?.qtype === 'text' ? (question.correct || []).join(', ') : '');
  const [numValue, setNumValue] = useState(question?.qtype === 'number' ? (question.correct?.value ?? '') : '');
  const [numTol, setNumTol] = useState(question?.qtype === 'number' ? (question.correct?.tolerance ?? 0) : 0);
  const [points, setPoints] = useState(question?.points || 1);
  const [explanation, setExplanation] = useState(question?.explanation || '');
  const [busy, setBusy] = useState(false);

  const setOpt = (id, val) => setOptions((o) => o.map((x) => x.id === id ? { ...x, text: val } : x));
  const addOpt = () => setOptions((o) => [...o, { id: 'o' + (o.length + 1) + Math.floor(Math.random() * 1000), text: '' }]);
  const rmOpt = (id) => setOptions((o) => o.filter((x) => x.id !== id));
  const toggleCorrect = (id) => setCorrectSet((s) => {
    const n = new Set(qtype === 'single' ? [] : s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const buildCorrect = () => {
    if (isChoice(qtype)) return [...correctSet];
    if (qtype === 'bool') return [boolCorrect];
    if (qtype === 'text') return textCorrect.split(/[,\n;]+/).map((s) => s.trim()).filter(Boolean);
    if (qtype === 'number') return { value: Number(numValue), tolerance: Number(numTol) || 0 };
    return [];
  };

  const save = useCallback(async () => {
    if (!text.trim()) { toast.show('Введите текст вопроса', 'warning'); return; }
    setBusy(true);
    const payload = {
      text, qtype, points: Number(points) || 1, explanation,
      options: isChoice(qtype) ? options.filter((o) => o.text.trim()) : [],
      correct: buildCorrect(),
    };
    try {
      if (isNew) await api.createQuestion(quizId, payload); else await api.updateQuestion(question.id, payload);
      toast.show('Вопрос сохранён', 'success'); onSaved(true);
    } catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); } finally { setBusy(false); }
    // eslint-disable-next-line
  }, [text, qtype, points, explanation, options, correctSet, boolCorrect, textCorrect, numValue, numTol]);

  const del = useCallback(async () => {
    if (!await confirm({ title: 'Удалить вопрос?', confirmLabel: 'Удалить', danger: true })) return;
    try { await api.deleteQuestion(question.id); onSaved(true); } catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  }, [question, confirm, onSaved, toast]);

  return (
    <div className="acad-q">
      <div className="acad-q__head">
        <span className="acad-q__num num">{isNew ? '+' : num}</span>
        <span className="acad-q__type">{QTYPE_LABEL[qtype]}</span>
      </div>
      <Field label="Вопрос"><Textarea value={text} onChange={(e) => setText(e.target.value)} minRows={2} /></Field>
      <div className="acad-grid-form">
        <Field label="Тип"><NativeSelect value={qtype} onChange={(e) => setQtype(e.target.value)} options={QTYPES} /></Field>
        <Field label="Баллы"><input className="input" type="number" min="1" value={points} onChange={(e) => setPoints(e.target.value)} /></Field>
      </div>

      {isChoice(qtype) && (
        <div className="stack stack-3">
          <div className="acad-correct-hint"><Icon name="check-circle" size={12} />Отметьте правильные варианты</div>
          {options.map((o) => (
            <div key={o.id} className="acad-opt">
              <Button className="acad-opt__toggle" size="sm" variant={correctSet.has(o.id) ? 'primary' : 'ghost'}
                onClick={() => toggleCorrect(o.id)}>{correctSet.has(o.id) ? '✓ верный' : 'верный?'}</Button>
              <TextInput value={o.text} onChange={(e) => setOpt(o.id, e.target.value)} className="grow" placeholder="Текст варианта" />
              <Button size="sm" variant="ghost" iconLeft="trash" onClick={() => rmOpt(o.id)} aria-label="Убрать вариант" />
            </div>
          ))}
          <div><Button size="sm" variant="ghost" iconLeft="plus" onClick={addOpt}>Вариант</Button></div>
        </div>
      )}
      {qtype === 'bool' && (
        <div className="cluster cluster-8">
          <Button size="sm" variant={boolCorrect ? 'primary' : 'ghost'} onClick={() => setBoolCorrect(true)}>Верно</Button>
          <Button size="sm" variant={!boolCorrect ? 'primary' : 'ghost'} onClick={() => setBoolCorrect(false)}>Неверно</Button>
        </div>
      )}
      {qtype === 'text' && (
        <Field label="Принимаемые ответы (через запятую)" hint="Сравнение без регистра и лишних пробелов">
          <TextInput value={textCorrect} onChange={(e) => setTextCorrect(e.target.value)} placeholder="эспрессо, espresso" /></Field>
      )}
      {qtype === 'number' && (
        <div className="acad-grid-form">
          <Field label="Значение"><input className="input" type="number" value={numValue} onChange={(e) => setNumValue(e.target.value)} /></Field>
          <Field label="Допуск ±"><input className="input" type="number" value={numTol} onChange={(e) => setNumTol(e.target.value)} /></Field>
        </div>
      )}

      <Field label="Пояснение (показывается после ответа)"><TextInput value={explanation} onChange={(e) => setExplanation(e.target.value)} /></Field>
      <div className="cluster cluster-8" style={{ justifyContent: 'space-between' }}>
        <Button size="sm" variant="primary" loading={busy} onClick={save}>{isNew ? 'Добавить вопрос' : 'Сохранить'}</Button>
        {!isNew && <Button size="sm" variant="ghost" iconLeft="trash" onClick={del}>Удалить</Button>}
      </div>
    </div>
  );
}

function QuizSection({ lesson, reload, guard }) {
  const toast = useToast();
  const confirm = useConfirm();
  const quiz = lesson.quiz;
  const [adding, setAdding] = useState(false);
  // AD-12: заголовок и вступление теста бэк принимает и отдаёт ученику
  // (QuizPublicSerializer), но задать их было негде. Держим в локальном состоянии
  // и сохраняем по blur — как порог и число попыток рядом.
  const [title, setTitle] = useState(quiz?.title || '');
  const [intro, setIntro] = useState(quiz?.intro_md || '');

  // AD-8: тест меняет требования урока — завершившие курс вернутся в «Активна».
  const create = async () => {
    if (!await guard('в урок добавится тест')) return;
    try { await api.saveQuiz(lesson.id, { pass_threshold: 70 }); reload(); } catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  };
  const saveSettings = async (patch) => {
    try { await api.saveQuiz(lesson.id, patch); reload(); } catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  };
  const del = async () => {
    if (!await confirm({ title: 'Удалить тест?', body: 'Вместе с вопросами.', confirmLabel: 'Удалить', danger: true })) return;
    await deleteWithGuard({ del: (force) => api.deleteQuiz(lesson.id, force), confirm, toast,
      entity: 'этому тесту', onDone: reload });
  };

  if (!quiz) return <Button variant="secondary" iconLeft="plus" onClick={create}>Добавить тест</Button>;
  return (
    <div className="stack stack-4">
      {quizIsEmpty(quiz) && (
        <div className="acad-notice acad-notice--warn" role="status">
          <Icon name="alert-triangle" size={15} />
          <span>Добавьте вопросы — иначе ученик не увидит тест.</span>
        </div>
      )}
      <Field label="Заголовок теста" hint="видит ученик над вопросами · пусто — «Тест»">
        <TextInput value={title} onChange={(e) => setTitle(e.target.value)}
          onBlur={() => { if (title !== (quiz.title || '')) saveSettings({ title }); }}
          placeholder="Например: Проверка по модулю «Заготовки»" />
      </Field>
      <Field label="Вступление к тесту (Markdown)" hint="условия и напутствие перед первым вопросом">
        <Textarea value={intro} minRows={2} onChange={(e) => setIntro(e.target.value)}
          onBlur={() => { if (intro !== (quiz.intro_md || '')) saveSettings({ intro_md: intro }); }} />
      </Field>
      <div className="acad-grid-form">
        <Field label="Порог сдачи, %"><input className="input" type="number" min="0" max="100" defaultValue={quiz.pass_threshold}
          onBlur={(e) => saveSettings({ pass_threshold: Number(e.target.value) })} /></Field>
        <Field label="Попыток (0 — ∞)"><input className="input" type="number" min="0" defaultValue={quiz.max_attempts}
          onBlur={(e) => saveSettings({ max_attempts: Number(e.target.value) })} /></Field>
      </div>
      <Checkbox label="Показывать правильные ответы после сдачи" checked={quiz.show_correct}
        onChange={(e) => saveSettings({ show_correct: e.target.checked })} />
      {/* AD-12: поле хранилось и ничего не обещало — теперь им можно управлять.
          Само перемешивание делает бэк на выдаче теста ученику. */}
      <Checkbox label="Перемешивать вопросы при каждой попытке" checked={!!quiz.shuffle_questions}
        onChange={(e) => saveSettings({ shuffle_questions: e.target.checked })} />
      <div className="stack stack-3">
        {(quiz.questions || []).map((qq, i) => <QuestionEditor key={qq.id} quizId={quiz.id} question={qq} num={i + 1} onSaved={reload} />)}
      </div>
      {adding
        ? <QuestionEditor quizId={quiz.id} onSaved={() => { setAdding(false); reload(); }} />
        : <div className="cluster cluster-8" style={{ justifyContent: 'space-between' }}>
            <Button variant="secondary" iconLeft="plus" onClick={() => setAdding(true)}>Добавить вопрос</Button>
            <Button variant="ghost" size="sm" iconLeft="trash" onClick={del}>Удалить тест</Button>
          </div>}
    </div>
  );
}

function HomeworkSection({ lesson, reload, guard }) {
  const toast = useToast();
  const confirm = useConfirm();
  const hw = lesson.homework;
  const [title, setTitle] = useState(hw?.title || '');
  const [instr, setInstr] = useState(hw?.instructions_md || '');
  const [maxScore, setMaxScore] = useState(hw?.max_score || 100);
  // AD-12: allow_files бэк принимает и хранит — вкладки в UI не было, и выключить
  // приём файлов было нельзя. allow_files=false делает require_files бессмысленным,
  // поэтому второй чекбокс гасим вместе с первым.
  const [allowFiles, setAllowFiles] = useState(hw?.allow_files !== false);
  const [requireFiles, setRequireFiles] = useState(hw?.require_files || false);
  const [blocks, setBlocks] = useState(hw?.blocks_progression || false);

  const save = async () => {
    try {
      await api.saveHomework(lesson.id, {
        title, instructions_md: instr, max_score: Number(maxScore) || 100,
        allow_files: allowFiles, require_files: allowFiles && requireFiles,
        blocks_progression: blocks,
      });
      toast.show('ДЗ сохранено', 'success'); reload();
    } catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  };
  const del = async () => {
    if (!await confirm({ title: 'Удалить ДЗ?', confirmLabel: 'Удалить', danger: true })) return;
    await deleteWithGuard({ del: (force) => api.deleteHomework(lesson.id, force), confirm, toast,
      entity: 'этому заданию', onDone: reload });
  };
  // AD-8: ДЗ меняет требования урока — тот же откат выпускников, что и у теста.
  const add = async () => {
    if (!await guard('в урок добавится домашнее задание')) return;
    try { await api.saveHomework(lesson.id, { instructions_md: '' }); reload(); }
    catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  };

  if (!hw) return <Button variant="secondary" iconLeft="plus" onClick={add}>Добавить ДЗ</Button>;
  return (
    <div className="stack stack-4">
      <Field label="Заголовок задания" hint="видит ученик в карточке ДЗ · пусто — «Домашнее задание»">
        <TextInput value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="Например: Соберите техкарту на своё блюдо" />
      </Field>
      <Field label="Задание (Markdown)"><Textarea value={instr} onChange={(e) => setInstr(e.target.value)} minRows={3} /></Field>
      <div className="acad-grid-form">
        <Field label="Максимальный балл"><input className="input" type="number" min="1" value={maxScore} onChange={(e) => setMaxScore(e.target.value)} /></Field>
        <Field label="Файлы в ответе">
          <div className="stack stack-2">
            <Checkbox label="Разрешить прикладывать файлы" checked={allowFiles}
              onChange={(e) => setAllowFiles(e.target.checked)} />
            <Checkbox label="Файл обязателен" checked={allowFiles && requireFiles}
              disabled={!allowFiles} onChange={(e) => setRequireFiles(e.target.checked)} />
          </div>
        </Field>
      </div>
      <div>
        <Checkbox label="Блокировать прохождение до проверки куратором"
          checked={blocks} onChange={(e) => setBlocks(e.target.checked)} />
        <div className="t-caption text-mute" style={{ marginTop: 4 }}>
          Выкл — ученик идёт дальше сразу после отправки (проверка = обратная связь).
          Вкл — урок не пройден, пока куратор не примет ДЗ.
        </div>
      </div>
      <div className="cluster cluster-8" style={{ justifyContent: 'space-between' }}>
        <Button variant="primary" onClick={save}>Сохранить ДЗ</Button>
        <Button variant="ghost" size="sm" iconLeft="trash" onClick={del}>Удалить ДЗ</Button>
      </div>
    </div>
  );
}

// Дровер урока открывается только из редактора курса, но guard-заглушка нужна:
// без неё любой будущий вызов без пропа падал бы на «guard is not a function».
const ALWAYS_OK = async () => true;

function LessonEditor({ lessonId, courseId, courseTitle, modules = [], guard = ALWAYS_OK, onClose }) {
  const toast = useToast();
  const confirm = useConfirm();
  // Лимит видео приезжает с сервера (academy_video_max_mb из /me): держать его
  // копией во фронте уже приводило к подсказке «до 512 МБ» при реальных 300.
  // С 12.08.2026 потолок — 1,5 ГБ, и число тем более нельзя вшивать сюда.
  const { user } = useAuth();
  const [nonce, setNonce] = useState(0);
  // #2: цель переноса/копирования — другой КУРС, затем его модуль.
  const [targetCourse, setTargetCourse] = useState(courseId != null ? String(courseId) : '');
  const [moveTo, setMoveTo] = useState(''); // id модуля назначения
  const [courses, setCourses] = useState(null); // список курсов (ленивый)
  const q = useFakeQuery('academy-m-lesson-' + lessonId + '-' + nonce, () => api.getManageLesson(lessonId), [lessonId, nonce]);
  const lesson = q.data;
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  /* Пока видео конвертируется — перечитываем урок раз в 15 секунд. Без этого
     методист смотрит на «конвертируем» до тех пор, пока сам не закроет и не
     откроет урок: результат приезжает фоновой задачей, и сказать о нём странице
     некому. Таймер живёт ТОЛЬКО в этом состоянии — постоянный опрос карточки
     урока не нужен никому. */
  const converting = lesson && (lesson.video_convert_status === 'queued'
                                || lesson.video_convert_status === 'running');
  useEffect(() => {
    if (!converting) return undefined;
    let alive = true;
    /* Опрос идёт МИМО основного запроса: один 502 на деплое или обрыв мобильной
       сети в основном q подменял бы весь редактор экраном «Ошибка» и снимал
       интервал — на 30-минутной конвертации это почти гарантированно. Здесь
       ошибка глотается, а перечитать редактор просим только когда статус
       реально изменился. */
    const t = setInterval(() => {
      api.getManageLesson(lessonId).then((fresh) => {
        if (!alive || !fresh) return;
        if (fresh.video_convert_status !== lesson.video_convert_status
            || fresh.video_url !== lesson.video_url) setNonce((n) => n + 1);
      }).catch(() => {});
    }, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [converting, lessonId, lesson && lesson.video_convert_status, lesson && lesson.video_url]);

  /* «Удалить видео» во время конвертации снимает и её — методист, который хотел
     лишь убрать устаревший ролик, потерял бы полтора гигабайта загрузки. */
  const delVideo = useCallback(async () => {
    if (converting && !await confirm({
      title: 'Удалить видео и отменить конвертацию?',
      body: `Файл «${lesson.video_source_name || ''}» ещё конвертируется. Удаление снимет и его — придётся заливать заново.`,
      confirmLabel: 'Удалить и отменить', danger: true })) return;
    try { await api.deleteVideo(lessonId); reload(); }
    catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  }, [converting, lesson, lessonId, confirm, reload, toast]);

  // курсы для выбора цели — ленивая загрузка при первом рендере секции
  useEffect(() => {
    let alive = true;
    api.listCourses().then((cs) => { if (alive) setCourses(cs); }).catch(() => { if (alive) setCourses([]); });
    return () => { alive = false; };
  }, []);

  // модули курса-цели: текущий курс → берём из пропа modules; иной курс → грузим его дерево
  const sameCourse = String(targetCourse) === String(courseId);
  const targetTree = useFakeQuery(
    'academy-target-' + targetCourse,
    () => (targetCourse && !sameCourse ? api.getCourse(Number(targetCourse)) : Promise.resolve(null)),
    [targetCourse, sameCourse]);
  const targetModules = sameCourse ? modules : (targetTree.data?.modules || []);
  // при смене курса-цели сбрасываем выбранный модуль
  const [loadedTarget, setLoadedTarget] = useState(targetCourse);
  if (loadedTarget !== targetCourse) { setLoadedTarget(targetCourse); setMoveTo(''); }

  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [article, setArticle] = useState('');
  const [required, setRequired] = useState(true);
  // Бонус — отдельная отметка, а не «необязательный»: необязательный урок просто
  // открыт сразу (курс без обязательных проходится в любом порядке), плашку
  // «бонус» ученик видит только у отмеченных. Бонус всегда необязателен.
  const [bonus, setBonus] = useState(false);
  // AD-13: предпросмотр статьи в том же рендере, что видит ученик (ChatMarkdown
  // в .ac-prose — ровно то, что стоит в плеере). Раньше методист узнавал, как
  // легла разметка, только открыв урок учеником.
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadedId, setLoadedId] = useState(null);
  if (lesson && loadedId !== lesson.id) {
    setLoadedId(lesson.id);
    setTitle(lesson.title); setSummary(lesson.summary || '');
    setArticle(lesson.article_md || ''); setRequired(lesson.is_required);
    setBonus(!!lesson.is_bonus);
  }

  const saveContent = useCallback(async () => {
    // AD-8: обязательность и бонус — требования курса. И включение, и снятие
    // пересчитывают записи и двигают статусы завершивших.
    if (lesson && bonus !== !!lesson.is_bonus) {
      if (!await guard(bonus ? 'урок станет бонусным' : 'урок перестанет быть бонусным')) return;
    } else if (lesson && required !== lesson.is_required) {
      const what = required ? 'урок станет обязательным' : 'урок перестанет быть обязательным';
      if (!await guard(what)) return;
    }
    setBusy(true);
    try { await api.updateLesson(lessonId, { title, summary, article_md: article, is_required: required, is_bonus: bonus }); toast.show('Сохранено', 'success'); reload(); }
    catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); } finally { setBusy(false); }
  }, [lessonId, lesson, title, summary, article, required, bonus, guard, toast, reload]);

  const onVideo = useCallback(async (e) => {
    const input = e.target;
    const f = input.files?.[0]; if (!f) return;
    /* Размер проверяем ДО отправки. Раньше методист узнавал предел только после
       того, как файл целиком уехал на сервер (а 1,5 ГБ по мобильному интернету —
       это уже не минуты, а десятки минут), и получал сухое «Видео больше N».
       Значение — СЕРВЕРНОЕ (academy_video_max_mb из /me), а не литерал в
       подсказке, который уже разъезжался с бэком и обещал 512 МБ. */
    if (f.size > videoMaxMb(user) * 1024 * 1024) {
      toast.show(api.videoTooLargeText(f.size, user), 'danger');
      input.value = ''; // иначе тот же файл повторно не выберется
      return;
    }
    toast.show('Загрузка видео…', 'muted');
    // Сбрасываем ДО ответа: после «Ошибка конвертации — залейте заново» методист
    // выбирает тот же файл в том же <input>, а браузер не бросает change при
    // неизменившемся value (в onPoster так и сделано).
    input.value = '';
    try {
      const res = await api.uploadVideo(lessonId, f);
      /* Разные исходы — разные обещания. «Видео загружено» на файле, который
         ушёл в перекодировку, было бы неправдой: в уроке его пока нет, и
         методист, закрыв редактор, считал бы дело сделанным. */
      toast.show(res && (res.video_convert_status === 'queued'
                         || res.video_convert_status === 'running')
        ? 'Файл принят — конвертируем в MP4'
        : 'Видео загружено', 'success');
      reload();
    } catch (err) { toast.show(err?.detail || err?.message || 'Ошибка загрузки видео', 'danger'); }
  }, [lessonId, toast, reload]);

  const onPoster = useCallback(async (e) => {
    const input = e.target;
    const f = input.files?.[0]; if (!f) return;
    input.value = ''; // иначе та же картинка повторно не выберется
    /* Тоста «Загрузка превью…» тут НЕТ намеренно. У видео он оправдан: полтора
       гигабайта едут минутами, и без него непонятно, идёт ли что-то. Картинка
       весит сотни килобайт и приезжает мгновенно, а тосты «Загрузка…» не гаснут
       при успехе — рядом с видео-парой на экране копилась стопка из четырёх
       сообщений, где верхнее противоречило нижнему. */
    try { await api.uploadVideoPoster(lessonId, f); toast.show('Превью загружено', 'success'); reload(); }
    catch (err) { toast.show(err?.detail || err?.message || 'Ошибка загрузки превью', 'danger'); }
  }, [lessonId, toast, reload]);

  const delPoster = useCallback(async () => {
    try { await api.deleteVideoPoster(lessonId); toast.show('Превью снято', 'success'); reload(); }
    catch (err) { toast.show(err?.detail || err?.message || 'Ошибка', 'danger'); }
  }, [lessonId, toast, reload]);

  const onMaterial = useCallback(async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { await api.uploadMaterial(lessonId, f); toast.show('Материал добавлен', 'success'); reload(); }
    catch (err) { toast.show(err?.detail || err?.message || 'Ошибка загрузки', 'danger'); }
  }, [lessonId, toast, reload]);

  const delLesson = useCallback(async () => {
    if (!await confirm({ title: 'Удалить урок?', confirmLabel: 'Удалить', danger: true })) return;
    await deleteWithGuard({ del: (force) => api.deleteLesson(lessonId, force), confirm, toast,
      entity: 'этому уроку', onDone: () => onClose(true) });
  }, [lessonId, confirm, onClose, toast]);

  // Дублирование «на месте» (в текущем модуле) — как раньше, без выбора цели.
  const dupLesson = useCallback(async () => {
    try { await api.copyLesson(lessonId); toast.show('Урок продублирован', 'success'); onClose(true); }
    catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  }, [lessonId, toast, onClose]);
  // Перенос в выбранный модуль (того же ИЛИ другого курса) — #2.
  const doMove = useCallback(async () => {
    if (!moveTo) return;
    try { await api.moveLesson(lessonId, Number(moveTo)); toast.show('Урок перенесён', 'success'); onClose(true); }
    catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  }, [lessonId, moveTo, toast, onClose]);
  // Копирование в выбранный модуль (того же ИЛИ другого курса) — #2.
  const doCopyTo = useCallback(async () => {
    if (!moveTo) return;
    try { await api.copyLesson(lessonId, Number(moveTo)); toast.show('Урок скопирован', 'success'); onClose(true); }
    catch (e) { toast.show(e?.detail || e?.message || 'Ошибка', 'danger'); }
  }, [lessonId, moveTo, toast, onClose]);

  const footer = lesson
    ? <Button variant="primary" loading={busy} onClick={saveContent} className="grow">Сохранить контент</Button>
    : null;

  return (
    <Drawer open level={2} onClose={() => onClose(true)} size="lg" title={lesson?.title || 'Урок'} footer={footer}>
      {/* PL-15: контейнер для container-query — вёрстка секций считает ширину
          панели дровера, а не вьюпорта. */}
      <div className="acad-pane">
        <QueryBoundary loading={q.loading && !lesson} error={q.error} onRetry={reload}>
          {lesson && (
            <div className="stack stack-6">
              <Section icon="file-text" title="Контент">
                <Field label="Название урока"><TextInput value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
                <Field label="Подзаголовок"><TextInput value={summary} onChange={(e) => setSummary(e.target.value)} /></Field>
                <Field label="Статья (Markdown)">
                  <div className="acad-tabs" role="tablist" aria-label="Статья урока"
                    style={{ marginBottom: 'var(--space-6)' }}>
                    <button type="button" role="tab" aria-selected={!preview}
                      className={'acad-seg' + (!preview ? ' is-active' : '')}
                      onClick={() => setPreview(false)}>Текст</button>
                    <button type="button" role="tab" aria-selected={preview}
                      className={'acad-seg' + (preview ? ' is-active' : '')}
                      onClick={() => setPreview(true)}>Предпросмотр</button>
                  </div>
                  {preview
                    ? (article.trim()
                        ? <div className="acad-preview ac-prose"><ChatMarkdown source={article} /></div>
                        : <div className="acad-preview t-caption text-mute">Статья пустая — ученику этот блок не покажется.</div>)
                    : <Textarea value={article} onChange={(e) => setArticle(e.target.value)} minRows={6} />}
                </Field>
                <div className="stack stack-2">
                  <Checkbox label="Обязательный урок (входит в прогресс; следующие уроки откроются после него)" checked={required}
                    disabled={bonus} onChange={(e) => setRequired(e.target.checked)} />
                  <Checkbox label="Бонусный урок (плашка «бонус» у ученика, не входит в прогресс)" checked={bonus}
                    onChange={(e) => { setBonus(e.target.checked); if (e.target.checked) setRequired(false); }} />
                </div>
              </Section>

              <Section icon="camera" title="Видео">
                {/* Состояние перекодировки — ПЕРЕД плеером и не вместо него.
                    Пока считается новый файл, у урока остаётся прежний ролик
                    (бэк `video` до успеха не трогает), и подменять его пустотой
                    значило бы показать методисту урок сломанным, пока он цел. */}
                {lesson.video_convert_status === 'queued' || lesson.video_convert_status === 'running' ? (
                  <div className="acad-notice" role="status">
                    <Icon name="clock" size={15} />
                    <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                      {`Видео «${lesson.video_source_name || 'файл'}» конвертируем в MP4 — `}
                      браузеры не проигрывают этот формат. Обычно это минуты, тяжёлая
                      лекция в hevc может считаться дольше часа; можно закрыть урок и
                      вернуться позже.
                      {lesson.video_url && ' Пока показывается прежний ролик.'}
                    </span>
                  </div>
                ) : lesson.video_convert_status === 'failed' ? (
                  <div className="acad-notice acad-notice--warn" role="status">
                    <Icon name="alert-triangle" size={15} />
                    <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                      {lesson.video_convert_error
                        || 'Не удалось перекодировать видео.'}
                      {' Залейте файл заново — сообщение исчезнет.'}
                    </span>
                  </div>
                ) : null}
                {lesson.video_url ? (
                  <div className="acad-mediacard">
                    {/* poster в самом редакторе — чтобы методист видел ровно то,
                        что увидит ученик до нажатия «play», а не первый кадр. */}
                    <video controls preload="metadata" src={lesson.video_url}
                      poster={lesson.video_poster_url || undefined} />
                    <div className="cluster cluster-8">
                      <label className="acad-uploader" style={{ flex: '0 0 auto', padding: 'var(--space-6) var(--space-10)' }}>
                        <Icon name="upload" size={15} /><span>Заменить</span>
                        <input type="file" accept={api.videoAccept(user)} onChange={onVideo} /></label>
                      <Button size="sm" variant="ghost" iconLeft="trash"
                        onClick={delVideo}>Удалить видео</Button>
                    </div>
                    {/* Превью живёт ВНУТРИ блока видео и только при нём: отдельной
                        секцией оно предлагало бы загрузить заставку к ролику,
                        которого ещё нет (бэк такой запрос и отклоняет). */}
                    <div className="cluster cluster-8">
                      <label className="acad-uploader" style={{ flex: '0 0 auto', padding: 'var(--space-6) var(--space-10)' }}>
                        <Icon name="image" size={15} />
                        <span>{lesson.video_poster_url ? 'Заменить превью' : 'Загрузить превью'}</span>
                        <input type="file" accept={api.POSTER_ACCEPT} onChange={onPoster} /></label>
                      {lesson.video_poster_url
                        ? <Button size="sm" variant="ghost" iconLeft="trash" onClick={delPoster}>Снять превью</Button>
                        : <span className="acad-uploader__hint">{api.posterHint}</span>}
                    </div>
                  </div>
                ) : (
                  <label className="acad-uploader"><Icon name="upload" size={18} />
                    <div><div>Загрузить видео</div>
                      <div className="acad-uploader__hint">{api.videoLimitHint(user)}</div></div>
                    <input type="file" accept={api.videoAccept(user)} onChange={onVideo} /></label>
                )}
              </Section>

              <Section icon="file-text" title="Материалы" hint={(lesson.materials || []).length || null}>
                <div className="stack stack-2">
                  {(lesson.materials || []).map((m) => (
                    <div key={m.id} className={'acad-material-row' + (m.blocked ? ' is-blocked' : '')}>
                      <Icon name={m.blocked ? 'alert-triangle' : 'file-text'} size={15} />
                      <span className="acad-material-row__name">{m.name}</span>
                      {/* Материал вне allowlist ученику не отдаётся вовсе. Без этой
                          пометки методист видел бы урок комплектным и не узнал, что
                          файл нужно перезалить в разрешённом формате. */}
                      {m.blocked && (
                        <span className="acad-material-row__blocked t-caption">
                          {m.blocked_reason || 'Формат запрещён — ученику не виден'}
                        </span>
                      )}
                      <Button size="sm" variant="ghost" iconOnly iconLeft="trash" title="Удалить"
                        onClick={async () => { await api.deleteMaterial(m.id); reload(); }} aria-label="Удалить" />
                    </div>
                  ))}
                  <label className="acad-uploader"><Icon name="upload" size={16} /><span>Добавить файл</span>
                    <input type="file" onChange={onMaterial} /></label>
                </div>
              </Section>

              <Section icon="check-circle" title="Тест (авто-проверка)" hint={lesson.quiz ? `${(lesson.quiz.questions || []).length} вопр.` : null}>
                <QuizSection lesson={lesson} reload={reload} guard={guard} />
              </Section>

              <Section icon="edit" title="Домашнее задание (куратор)">
                <HomeworkSection lesson={lesson} reload={reload} guard={guard} />
              </Section>

              <Section icon="copy" title="Копировать / перенести">
                {/* Дублирование в текущем модуле — без выбора цели */}
                <div className="cluster cluster-8" style={{ flexWrap: 'wrap' }}>
                  <Button variant="secondary" iconLeft="copy" onClick={dupLesson}>Дублировать урок (в этом модуле)</Button>
                </div>
                {/* Выбор цели: курс → модуль; затем «Перенести» / «Копировать сюда» (#2) */}
                <div className="acad-grid-form">
                  <Field label="Курс назначения">
                    <NativeSelect value={targetCourse} onChange={(e) => setTargetCourse(e.target.value)}
                      disabled={courses == null}
                      options={(courses || []).map((c) => ({
                        value: String(c.id),
                        label: String(c.id) === String(courseId) ? `${c.title} (текущий)` : c.title,
                      }))} />
                  </Field>
                  <Field label="Модуль назначения" hint={!sameCourse && targetTree.loading ? 'загрузка модулей…' : null}>
                    <NativeSelect value={moveTo} onChange={(e) => setMoveTo(e.target.value)}
                      disabled={!sameCourse && targetTree.loading}
                      options={[{ value: '', label: '— выберите модуль —' },
                        ...targetModules
                          .filter((m) => !(sameCourse && m.id === lesson.module))
                          .map((m) => ({ value: String(m.id), label: m.title }))]} />
                  </Field>
                </div>
                <div className="cluster cluster-8" style={{ flexWrap: 'wrap' }}>
                  <Button variant="secondary" iconLeft="arrow-up-right" disabled={!moveTo} onClick={doMove}>Перенести</Button>
                  <Button variant="ghost" iconLeft="copy" disabled={!moveTo} onClick={doCopyTo}>Копировать сюда</Button>
                </div>
              </Section>

              <Section icon="trash" title="Опасная зона">
                <div className="set-card__danger">
                  <div className="stack stack-2"><span className="t-label">Удалить урок</span>
                    <span className="t-caption">Вместе с тестом, ДЗ и материалами.</span></div>
                  <Button variant="danger" iconLeft="trash" onClick={delLesson}>Удалить</Button>
                </div>
              </Section>
            </div>
          )}
        </QueryBoundary>
      </div>
    </Drawer>
  );
}

export { LessonEditor };
